import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { logErrorToSentry } from "@litres/utils";
import type { IReduxRequestNode, IResponseRedirect } from "@litres-libs/types";
import { Request } from "../../request/Request";

export interface ICustomSetRequest {
  action: "create_custom_set";
  arts?: string;
  js?: number;
  basket?: "true";
  buy_now?: "true";
}

export interface ISeriesCustomSetRequest {
  action: "create_sequence_set_all";
  sequence: number;
  gu_ajax: true;
  atype?: "audio" | "";
  js?: number;
}

export interface ICustomSetResponse {
  redirect?: IResponseRedirect;
  status: string;
  error_msg?: string;
  csrf: string;
  user_balance?: number;
  custom_set?: {
    id: number;
    cost: string;
    name: string;
  };
}

export type TCreateCustomSetNode = IReduxRequestNode<
  ICustomSetRequest,
  ICustomSetResponse
>;

/**
 * Создание custom set для оплаты
 */
export const createCustomSet = createAsyncThunk(
  "payment/actions/createCustomSet",
  async (params: {
    arts: number[];
    basket?: boolean;
    buy_now?: boolean;
    onSuccess?: (success: TCreateCustomSetNode) => void;
    onError?: (error: TCreateCustomSetNode) => void;
  }) => {
    const response = new Request.CSRF<ICustomSetRequest, ICustomSetResponse>({
      url: "/pages/ajax_empty2/",
      provider: {
        id: "createCustomSet",
      },
      request: {
        method: "POST",
        data: params.basket
          ? {
              action: "create_custom_set",
              basket: "true",
              buy_now: params.buy_now ? "true" : undefined,
            }
          : {
              action: "create_custom_set",
              js: 1,
              arts: params.arts.join(","),
            },
      },
      onSuccess: params.onSuccess,
      onError: error => {
        //логируем ошибку ручки в SENTRY, 500-ки или 400-rb
        logErrorToSentry("create_custom_set is failed", {
          contextProperties: ["params", { error: JSON.stringify(error) }],
          tags: { type: "critical" },
          logLevel: "fatal",
        });
        if (params.onError) params.onError(error);
      },
    });
    const result = await response.fetch();
    //отправляем текст ошибки b редирект в sentry
    if (result.response?.error_msg) {
      logErrorToSentry("create_custom_set is failed", {
        contextProperties: [
          "params",
          {
            error: JSON.stringify(result.response),
          },
        ],
        tags: { type: "critical" },
        logLevel: "fatal",
      });
    }
    return result;
  }
);

export const createSeriesCustomSet = createAsyncThunk(
  "payment/actions/createSeriesCustomSet",
  async (params: {
    sequence: number;
    atype?: "audio";
    onSuccess?: (success: TCreateCustomSetNode) => void;
    onError?: (error: TCreateCustomSetNode) => void;
  }) => {
    const response = new Request.CSRF<
      ISeriesCustomSetRequest,
      ICustomSetResponse
    >({
      url: "/pages/ajax_empty2/",
      provider: {
        id: "createSeriesCustomSet",
      },
      request: {
        method: "POST",
        data: {
          action: "create_sequence_set_all",
          js: 1,
          gu_ajax: true,
          sequence: params.sequence,
          atype: params.atype ? "audio" : "",
        },
      },
      onSuccess: params.onSuccess,
      onError: error => {
        //логируем ошибку ручки в SENTRY, 500-ки или 400-rb
        logErrorToSentry("create_series_custom_set is failed", {
          contextProperties: [
            "params",
            {
              error: JSON.stringify(error),
            },
          ],
          tags: { type: "critical" },
          logLevel: "error",
        });
        if (params.onError) params.onError(error);
      },
    });
    const result = await response.fetch();
    //отправляем текст ошибки b редирект в sentry
    if (result.response?.error_msg) {
      logErrorToSentry("create_series_custom_set is failed", {
        contextProperties: [
          "params",
          {
            error: JSON.stringify(result.response),
          },
        ],
        tags: { type: "critical" },
        logLevel: "error",
      });
    }
    return result;
  }
);

export interface IPodcastSetRequest {
  action: "create_podcast_set_all";
  js: 1;
  podcast: number;
}

export interface IPodcastSetResponse {
  status: string;
  error_msg?: string;
  csrf: string;
  custom_set?: {
    discount: string;
    id: number;
    name: string;
    cost: string;
  };
  user_balance?: number;
}

export type TCreatePodcastSetNode = IReduxRequestNode<
  IPodcastSetRequest,
  IPodcastSetResponse
>;

/**
 * Создание custom set для покупки всех эпизодов выпуска
 */
export const createPodcastSet = createAsyncThunk(
  "payment/actions/createPodcastSet",
  async (params: {
    podcastId: number;
    onSuccess?: (success: TCreatePodcastSetNode) => void;
    onError?: (error: TCreatePodcastSetNode) => void;
  }) => {
    const response = new Request.CSRF<IPodcastSetRequest, IPodcastSetResponse>({
      url: "/pages/ajax_empty2/",
      provider: {
        id: "createPodcastSet",
      },
      request: {
        method: "POST",
        data: {
          action: "create_podcast_set_all",
          js: 1,
          podcast: params.podcastId,
        },
      },
      onSuccess: params.onSuccess,
      onError: error => {
        //логируем ошибку ручки в SENTRY, 500-ки или 400-ки
        logErrorToSentry("create_podcast_set is failed", {
          contextProperties: ["params", { error: JSON.stringify(error) }],
          tags: { type: "critical" },
          logLevel: "fatal",
        });
        if (params.onError) params.onError(error);
      },
    });

    const result = await response.fetch();
    //отправляем текст ошибки в sentry
    if (result.response?.error_msg) {
      logErrorToSentry("create_podcast_set is failed", {
        contextProperties: [
          "params",
          { error: JSON.stringify(result.response) },
        ],
        tags: { type: "critical" },
        logLevel: "fatal",
      });
    }
    return result;
  }
);

export interface IDoOrderRequest {
  action: "do_order";
  custom_set: number;
}

export interface IDoOrderResponse {
  status: string;
  error_msg?: string;
  csrf: string;
}

export type TDoOrderNode = IReduxRequestNode<IDoOrderRequest, IDoOrderResponse>;

/**
 * Покупка после создания custom set
 */
export const doOrder = createAsyncThunk(
  "payment/actions/doOrder",
  async (params: {
    customSetId: number;
    onSuccess?: (success: TDoOrderNode) => void;
    onError?: (error: TDoOrderNode) => void;
  }) => {
    const response = new Request.CSRF<IDoOrderRequest, IDoOrderResponse>({
      url: "/pages/ajax_empty2/",
      provider: {
        id: "doOrder",
      },
      request: {
        method: "POST",
        data: {
          action: "do_order",
          custom_set: params.customSetId,
        },
      },
      onSuccess: params.onSuccess,
      onError: error => {
        //логируем ошибку ручки в SENTRY, 500-ки или 400-ки
        logErrorToSentry("do_order is failed", {
          contextProperties: ["params", { error: JSON.stringify(error) }],
          tags: { type: "critical" },
          logLevel: "fatal",
        });
        if (params.onError) params.onError(error);
      },
    });

    const result = await response.fetch();
    //отправляем текст ошибки в sentry
    if (result.response?.error_msg) {
      logErrorToSentry("do_order is failed", {
        contextProperties: [
          "params",
          { error: JSON.stringify(result.response) },
        ],
        tags: { type: "critical" },
        logLevel: "fatal",
      });
    }
    return result;
  }
);

export const paymentSlice = createSlice({
  name: "payment/actions",
  initialState: null,
  reducers: {},
});

export default paymentSlice.reducer;
